import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronDown } from 'lucide-react';
import TypeChip from './TypeChip';

const colorFor = (status) => {
  switch (status) {
    case 'major':  return 'border-ter-red bg-red-50 text-red-900';
    case 'minor':  return 'border-amber-300 bg-amber-50 text-amber-900';
    case 'normal': return 'border-emerald-300 bg-emerald-50 text-emerald-900';
    default:       return 'border-slate-200 bg-slate-50 text-slate-700';
  }
};

const effectToKey = (effect) => {
  const e = (effect || '').toUpperCase();
  switch (e) {
    case 'NO_SERVICE':           return 'no_service';
    case 'SIGNIFICANT_DELAYS':   return 'significant_delays';
    case 'REDUCED_SERVICE':      return 'reduced_service';
    case 'DETOUR':               return 'detour';
    case 'MODIFIED_SERVICE':     return 'modified_service';
    case 'ADDITIONAL_SERVICE':   return 'additional_service';
    case 'STOP_MOVED':           return 'stop_moved';
    case 'OTHER_EFFECT':         return 'other_effect';
    default:                     return 'unknown';
  }
};

const DisruptionsBanner = ({ disruptions, station }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  if (!disruptions || disruptions.length === 0) return null;

  const hasMajor = disruptions.some(d => d.status === 'major');

  return (
    <div
      className={`border-l-4 rounded-md px-4 py-3 mb-3 ${hasMajor ? colorFor('major') : colorFor('minor')}`}
      data-testid="disruptions-banner"
    >
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between gap-3"
        data-testid="disruptions-toggle"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <AlertTriangle className={`w-4 h-4 shrink-0 ${hasMajor ? 'text-ter-red' : 'text-amber-700'}`} />
          <div className="font-bold text-sm leading-tight text-left">
            {t('disruptions.banner_title', { count: disruptions.length })}
            {station && <span className="text-slate-600 font-medium"> — {station}</span>}
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <ul className="mt-2.5 space-y-1.5" data-testid="disruptions-list">
          {disruptions.map((dr, idx) => {
            const hasTrain = dr.train_number && dr.train_time;
            return (
            <li
              key={dr.id || idx}
              className="text-xs leading-snug pl-6 relative"
              data-testid={`disruption-item-${idx}`}
            >
              <span className={`absolute left-0 top-1 w-2 h-2 rounded-full ${dr.status === 'major' ? 'bg-ter-red' : 'bg-amber-400'}`} />
              {hasTrain && (
                <span className="inline-flex items-center gap-1.5 mr-1.5 font-mono font-bold tabular-nums">
                  <TypeChip type={dr.type_train || 'TER'} marque={dr.marque || ''} modeTransport={dr.modeTransport || ''} origin={dr.train_origin || ''} destination={dr.train_destination || ''} number={dr.train_number || ''} />
                  <span>{dr.train_number}</span>
                  <span className="text-slate-500">•</span>
                  <span className={`${dr.train_delay > 0 ? 'text-ter-red' : 'text-ter-blue'}`}>{dr.train_time}</span>
                  {dr.train_delay > 0 && dr.train_real_time && dr.train_real_time !== '—' && (
                    <span className="text-ter-red font-semibold">→ {dr.train_real_time}</span>
                  )}
                  {(dr.train_destination || dr.train_origin) && (
                    <span className="text-slate-500 font-normal ml-1">({dr.train_destination || dr.train_origin})</span>
                  )}
                </span>
              )}
              <strong className="font-bold">{t(`disruptions.effect.${effectToKey(dr.severity_effect)}`, { defaultValue: dr.label || t('disruptions.unknown_label') })}</strong>
              {dr.detail && dr.detail !== '—' && (
                <span className="text-slate-700"> — {dr.detail}</span>
              )}
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default DisruptionsBanner;
